import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const auth = token => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} });

// -- catalogue ----------------------------------------------------------------

export const listItems = ({ activeOnly = true, category = null } = {}, token) =>
  axios
    .get(`${API}/inventory/items`, {
      ...auth(token),
      params: { active_only: activeOnly, ...(category ? { category } : {}) },
    })
    .then(r => r.data);

export const createItem = (body, token) =>
  axios.post(`${API}/inventory/items`, body, auth(token)).then(r => r.data);

export const updateItem = (itemId, patch, token) =>
  axios.patch(`${API}/inventory/items/${itemId}`, patch, auth(token)).then(r => r.data);

// -- levels -------------------------------------------------------------------

/**
 * `on_hand` is derived by the server from SUM(stock_movements.quantity). No column
 * caches it, so this endpoint is the only truth about a stock level — never add a
 * movement's delta to a local copy and call the result the new on-hand.
 */
export const stockLevels = (activeOnly = true, token) =>
  axios
    .get(`${API}/inventory/stock`, { ...auth(token), params: { active_only: activeOnly } })
    .then(r => r.data);

export const lowStock = token =>
  axios.get(`${API}/inventory/low-stock`, auth(token)).then(r => r.data);

// -- movements ----------------------------------------------------------------

/**
 * Which direction each movement type is allowed to move stock. Mirrors
 * inventory_service._POSITIVE_ONLY / _NEGATIVE_ONLY: 1 adds, -1 removes, 0 means
 * only the operator can say.
 *
 * The server rejects the wrong sign with a 400 rather than helpfully flipping it —
 * a silently corrected sign is how a typo becomes inventory that was never bought.
 */
export const MOVEMENT_DIRECTION = {
  purchase: 1,
  opening: 1,
  consumption: -1,
  wastage: -1,
  adjustment: 0,
};

export const MOVEMENT_TYPES = [
  ["purchase", "Purchase — adds stock"],
  ["opening", "Opening balance — adds stock"],
  ["consumption", "Consumption — removes stock"],
  ["wastage", "Wastage — removes stock"],
  ["adjustment", "Adjustment — you choose the direction"],
];

/**
 * Turn the magnitude an operator typed into the signed quantity the API takes.
 *
 * Returns null when the request would be ambiguous — an unknown type, a zero or
 * non-numeric magnitude, or an adjustment whose direction nobody chose — so a
 * caller cannot post a movement whose direction was guessed on its behalf.
 */
export const signedQuantity = (movementType, magnitude, direction = null) => {
  const sign = MOVEMENT_DIRECTION[movementType];
  if (sign === undefined) return null;
  const size = Math.abs(Number(magnitude));
  if (!Number.isFinite(size) || size === 0) return null;
  if (sign === 0) {
    if (direction !== 1 && direction !== -1) return null;
    return size * direction;
  }
  return size * sign;
};

/** `quantity` must already be signed — use signedQuantity() to build it. */
export const createMovement = (body, token) =>
  axios.post(`${API}/inventory/movements`, body, auth(token)).then(r => r.data);

export const listMovements = (
  { stockItemId = null, movementType = null, dateFrom = null, dateTo = null, limit = 100 } = {},
  token
) =>
  axios
    .get(`${API}/inventory/movements`, {
      ...auth(token),
      params: {
        ...(stockItemId ? { stock_item_id: stockItemId } : {}),
        ...(movementType ? { movement_type: movementType } : {}),
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
        limit,
      },
    })
    .then(r => r.data);

/** Admin only — what the stock on hand cost is a P&L figure, not a service-floor one. */
export const valuation = token =>
  axios.get(`${API}/inventory/valuation`, auth(token)).then(r => r.data);

// -- owner brief 2026-11-01: units, stores, buy units, counts --------------------

/** The fixed stock units (B3). The server refuses anything else. */
export const STOCK_UNITS = ["ML", "LTR", "GM", "KG", "PCS", "PKT", "BTL", "BOX", "TIN", "ROLL", "BUNCH"];

export const listStores = token =>
  axios.get(`${API}/inventory/departments`, auth(token)).then(r => r.data);

export const listBuyUnits = (itemId, token) =>
  axios.get(`${API}/inventory/items/${itemId}/buy-units`, auth(token)).then(r => r.data);

export const setBuyUnits = (itemId, units, token) =>
  axios.put(`${API}/inventory/items/${itemId}/buy-units`, { units }, auth(token)).then(r => r.data);

export const listCounts = (departmentId, token) =>
  axios.get(`${API}/inventory/counts`, { ...auth(token), params: departmentId ? { department_id: departmentId } : {} })
    .then(r => r.data);

export const createCount = (body, token) =>
  axios.post(`${API}/inventory/counts`, body, auth(token)).then(r => r.data);

export const getCount = (countId, token) =>
  axios.get(`${API}/inventory/counts/${countId}`, auth(token)).then(r => r.data);

/** `counted_qty` null = not counted. A blank box is never sent as 0. */
export const setCountLines = (countId, lines, token) =>
  axios.put(`${API}/inventory/counts/${countId}/lines`, { lines }, auth(token)).then(r => r.data);

export const postCount = (countId, token) =>
  axios.post(`${API}/inventory/counts/${countId}/post`, {}, auth(token)).then(r => r.data);

export const deleteCount = (countId, token) =>
  axios.delete(`${API}/inventory/counts/${countId}`, auth(token)).then(r => r.data);
